import React, { useMemo, useRef, useCallback, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useGetPurchaseQuery } from '../../apis/mlkApi/mlk.api.hooks';
import { Button } from '../../components/Button/Button';
import { BaseLayout } from '../../components/Layout/Layout';
import { ModalRef } from '../../components/Modal/Modal';
import { SeparatorLine } from '../../components/SeparatorLine/SeparatorLine';
import { getExpectedServiceDateAndTimeSlot } from '../../helpers/order';
import { useBack } from '../../hooks/useBack';
import { useOrderDetails } from '../../hooks/useOrderDetails';
import { useLanguage } from '../../locale/useLanguage';
import { Routes } from '../../routes/types';
import { ProductItem } from './components/ProductItem/ProductItem';
import { RefundModal } from './components/RefundModal/RefundModal';
import {
  getIsHelpButtonVisible,
  getIsRefundButtonVisible,
} from './orderDetailsHelper';

import './OrderDetailsView.css';

type OrderDetailsRouteParams = {
  transactionId?: string;
};

type OrderDetailsViewProps = {};

export const OrderDetailsView: React.FC<OrderDetailsViewProps> = () => {
  const [t] = useLanguage();
  const back = useBack();
  const history = useHistory();
  const refundModalRef = useRef<ModalRef>(null);
  const { transactionId } = useParams<OrderDetailsRouteParams>();

  const order = useOrderDetails();
  const { data: purchase } = useGetPurchaseQuery(transactionId || '', {
    skip: !transactionId,
  });

  useEffect(() => {
    if (!transactionId) {
      history.replace(Routes.Home);
    }
  }, [history, transactionId]);

  const expectedServiceDateAndTimeSlot = useMemo(
    () => getExpectedServiceDateAndTimeSlot(order),
    [order]
  );

  const openRefundModal = useCallback(() => {
    refundModalRef.current?.openModal();
  }, []);

  const goToHelp = useCallback(() => {
    window.location.href = 'https://mlkdeliveries.it/assistenza-destinatario/';
    // if (order) {
    //   history.push(Routes.OrderHelp, {
    //     orderId: order?.id,
    //     purchaseId: purchase?.id,
    //   });
    // }
  }, []);

  const billingName =
    purchase?.billingAddress.companyName ||
    `${purchase?.billingAddress.name} ${purchase?.billingAddress.surname}`;

  const billingAddress = useMemo(() => {
    if (purchase?.billingAddress.company) {
      return `${purchase?.billingAddress.companyAddress}, ${purchase?.billingAddress.companyMunicipality},  ${purchase?.billingAddress.companyCountryCode}`;
    } else if (purchase?.billingAddress) {
      return `${purchase?.billingAddress.residentialAddress}, ${purchase?.billingAddress.municipality}, ${purchase?.billingAddress.countryCode}`;
    }
  }, [purchase?.billingAddress]);

  const taxCode =
    purchase?.billingAddress.vatNumber || purchase?.billingAddress.fiscalCode;

  const isRefundButtonVisible = getIsRefundButtonVisible(purchase);
  const isRefundAsked = purchase?.refundRequested;
  const isHelpButtonVisible = getIsHelpButtonVisible(purchase);

  return (
    <BaseLayout
      onBackClick={back}
      hasBack
      title={t('pageTitles.orderPaymentsDetails')}
    >
      {order && (
        <>
          <div className="order-details--info-container">
            <p className="order-details--shipment">
              {t('orderDetails.delivery')}
            </p>
            <p className="order-details--title">
              {order.shipper.alias || order.shipper.name}
            </p>
            <p className="order-details--text">{order.shipperOrderCode}</p>
            <p className="order-details--text">
              {expectedServiceDateAndTimeSlot?.to.format('DD MMMM YYYY')}
            </p>
          </div>
          <SeparatorLine />
          {purchase?.items?.map((pi) => {
            return <ProductItem key={pi.id} purchaseItem={pi} />;
          })}
          <div className="order-details--info-container">
            {purchase?.billingAddress ? (
              <>
                <p className="order-details--title">
                  {t('orderDetails.taxData')}
                </p>
                <p className="order-details--text">{billingName}</p>
                <p className="order-details--text">{billingAddress}</p>
                <p className="order-details--text">{taxCode}</p>
              </>
            ) : (
              <p>-</p>
            )}
          </div>
          <div className="order-details--info-container">
            <p className="order-details--title">
              {t('orderDetails.paymentMethod')}
            </p>
            {purchase && purchase.total > 0 ? (
              <p className="order-details--text">
                {t('orderDetails.paymentMethodCard')}
              </p>
            ) : (
              <p className="order-details--text">-</p>
            )}
          </div>
          {isHelpButtonVisible && (
            <div
              className={
                !isRefundButtonVisible && !isRefundAsked
                  ? 'order-details--button'
                  : ''
              }
            >
              <Button
                onClick={goToHelp}
                isPrimary
                label={t('orderDetails.requestHelp')}
              />
            </div>
          )}
          {isRefundButtonVisible && (
            <div className="order-details--button">
              <Button
                onClick={openRefundModal}
                label={t('orderDetails.requestRefund')}
              />
            </div>
          )}
          {isRefundAsked && (
            <div className="order-details--button">
              <Button isDisabled label={t('orderDetails.refundAsked')} />
            </div>
          )}
        </>
      )}
      <RefundModal
        ref={refundModalRef}
        orderId={order?.id}
        purchaseId={purchase?.id}
      />
    </BaseLayout>
  );
};
